import React, { useContext, useState, useRef, useLayoutEffect } from 'react';
import { useTransition, animated } from 'react-spring';
import useMeasure from 'react-use-measure';
import ResizeObserver from 'resize-observer-polyfill';
import _merge from 'lodash/merge';
import { TRANSITION_ACCELERATE } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';
import { SPEED_NORMAL } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import { SPACING_12 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/spacing';
import classnames from 'classnames';
import { getContext, useCombinedRefs, isAnimationDisabled } from 'common';
import { getEasing, getDuration } from 'common/parse-tokens';
import { ListContext } from 'components/List';
import { CONSTANTS } from 'common/ListKeyboardNav';

import Tooltip, { TooltipProps } from 'components/Tooltip/Tooltip';
import {
  StepTitle,
  StepDetail,
  StepTextContainer,
  StepFormContext,
  StepDropDownButton,
  StepLine,
} from 'components/StepForm';
import {
  getIndicatorForVariant,
  getPressedColor,
  isSelectionKeyPressed,
} from './common/functions';

import DeprecatedRippleEffect from '../RippleEffect/DeprecatedRippleEffect';
import Styled, { LAST_STEP_SELECTOR } from './styles';

const {
  LIST_ITEM_TABABLE_ITEM,
  LIST_ITEM_SELECTED,
} = CONSTANTS;

const SPRING_CONFIG = {
  duration: getDuration(SPEED_NORMAL),
  easing: getEasing(TRANSITION_ACCELERATE)
};

const TRANSITION_ANIMATION = (height, resolvedHeight, immediate) => ({
  from: { height: 0 },
  enter: { height: resolvedHeight },
  leave: { height: 0 },
  update: { height },
  unique: true,
  config: SPRING_CONFIG,
  immediate,
});

// max number of lines allowed for details
const MAX_DETAIL_LINES = 3;
const DETAIL_LINE_HEIGHT = 15;

export interface StepContextInterface {
  disabled?: boolean;
  variant?: 'current' | 'future' | 'error' | 'warning' | 'completed';
  isOpen?: boolean;
  isLinear?: boolean;
  isPressed?: boolean;
  disableAnimation?: boolean,
}

const StepContext = React.createContext<StepContextInterface | null>({});

export interface StepProps {
  /** Override the default indicator used for the Step variant. Consider using one of the step indicator components (StepIndicator, StepErrorIndicator, StepWarningIndicator, StepCompletedIndicator). */
  indicator?: React.ReactNode;
  /** Apply properties to the default indicator. */
  indicatorProps?: {};
  /** Step number for any linear step indicator that uses it. Only applies when using default indicator. */
  stepNumber?: string | number;
  /** The title for the step. */
  title: string;
  /** The extra details for the step. */
  details?: string;
  /** Any substeps for this `Step`. Consider using `SubStep` component. */
  children?: React.ReactNode;
  /** Stet to true if this step is the last in the StepForm. Used for styling. */
  lastStep?: boolean;
  /** If true, the substeps, if any, are expanded. */
  isOpen?: boolean;
  /** The current selected step. Usually it is the variant=current step, unless it has error or warning. */
  selected?: boolean;
  /** The variant for styling purposes.  */
  variant?: 'current' | 'future' | 'error' | 'warning' | 'completed';
  /* If true, the step is disabled. */
  disabled?: boolean;
  /** Control the animation. Leverages 'react-spring' useTransition. See https?://react-spring.io/ */
  animationProps?: any;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** If `true`, the button will be rendered without ripple effect. */
  disabledRipple?: boolean;
  /** Properties for the ripple style. */
  rippleStyle?: {};
  /** Props to pass to the internal Step element. */
  internalStepProps?: Partial<React.ComponentPropsWithRef<'div'>>;
  /** If true, disable any tooltip for details text. */
  disableTooltip?: boolean;
  /** Override tooltip properties for details text.  */
  tooltipProps?: Partial<TooltipProps>;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** @ignore */
  onClick?: React.MouseEventHandler;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
  /** @ignore */
  onKeyUp?: React.KeyboardEventHandler;
  /** @ignore */
  onMouseUp?: React.MouseEventHandler;
  /** @ignore */
  onMouseDown?: React.MouseEventHandler;
  /** @ignore */
  onMouseLeave?: React.MouseEventHandler;
  /** @ignore */
  onBlur?: React.FocusEventHandler;
}

/** `Step`, all other props will be provided to the root element */
const Step = React.forwardRef<HTMLElement, StepProps>((props: StepProps, ref) => getContext(({ RTL, theme, disableAnimation }) => {
  const {
    indicator,
    indicatorProps,
    stepNumber,
    title,
    details,
    disableTooltip,
    tooltipProps,
    selected,
    variant,
    disabled,
    theme: themeProp,
    animationProps,
    disableAnimation: propsdisableAnimation,
    disabledRipple,
    rippleStyle,
    children,
    lastStep,
    isOpen,
    internalStepProps,
    ...otherProps
  } = props;
  const [enableTextTooltip, setEnableTextTooltip] = useState(undefined);
  const [numberOfLines, setNumberOfLines] = useState(0);
  const [isPressed, setIsPressed] = useState(false);
  const tooltipText = useRef(undefined);
  const rippleRef = useRef(null);
  const wrapperRef = useRef(null);
  const stepDetailsRef = useRef(null);
  const listContext = useContext(ListContext);
  const stepFormContext = useContext(StepFormContext);
  const [heightRef, { height }] = useMeasure({ polyfill: ResizeObserver });

  const { ref: InternalStepPropsRef, ...otherInternalStepProps } = internalStepProps;
  const combinedRefs = useCombinedRefs(wrapperRef, InternalStepPropsRef);
  const resolvedTheme = _merge({}, theme.STEP_FORM, themeProp && themeProp.STEP_FORM ? themeProp.STEP_FORM : {});
  const [pressedColor, setPressedColor] = useState(resolvedTheme.pressed.background);

  const resolvedDisableAnimation = isAnimationDisabled(propsdisableAnimation, disableAnimation);
  const isLinear = (stepNumber !== undefined);
  const expandedTransition: any = useTransition(
    isOpen,
    null,
    animationProps || TRANSITION_ANIMATION(height, height, resolvedDisableAnimation)
  );

  const TOOLTIP_PROPS: any = {
    placement: 'bottom',
    trigger: ['hover', 'focus'],
    tooltip: tooltipText.current || '',
    triggerRef: ref,
    modifiers: { offset: { offset: '0, 10' } },
    disable: !enableTextTooltip || disableTooltip,
    ...tooltipProps
  };

  const stepHeight = stepDetailsRef && stepDetailsRef.current && stepDetailsRef.current.offsetHeight;
  useLayoutEffect(() => {
    const countLines = () => {
      const el = stepDetailsRef && stepDetailsRef.current;
      if (el === null) return 0;
      const divHeight = el.offsetHeight;
      const lineHeight = DETAIL_LINE_HEIGHT;
      const lines = divHeight / lineHeight;
      if (lines > MAX_DETAIL_LINES) return MAX_DETAIL_LINES;
      return lines;
    };
    if (details) {
      const lines = countLines(); // number of lines details text occupy (for styling purposes)
      setNumberOfLines(lines);
    }
  }, [details, enableTextTooltip, stepHeight]);

  useLayoutEffect(() => {
    const countLines = () => {
      if (details) {
        const el = stepDetailsRef && stepDetailsRef.current;
        if (el === null) return 0;
        const divHeight = el.offsetHeight;
        const lineHeight = DETAIL_LINE_HEIGHT;
        let lines = divHeight / lineHeight;
        if (lines > MAX_DETAIL_LINES) {
          lines = MAX_DETAIL_LINES;
        }
        setNumberOfLines(lines);
      }
      return null;
    };
    window.addEventListener('resize', countLines);
    return () => {
      window.removeEventListener('resize', countLines);
    };
  },);

  const handleEnableTooltip = (clampedObj) => {
    setEnableTextTooltip(clampedObj.clamped);
    tooltipText.current = undefined;
    if (clampedObj.clamped) {
      tooltipText.current = clampedObj.fullText;
    }
  };

  const handleOnKeyDown = (event) => {
    const { onKeyDown } = otherProps;
    if (isSelectionKeyPressed(event.key) && !disabled) {
      setPressedColor(getPressedColor(resolvedTheme, variant, false));
      setIsPressed(true);
      rippleRef.current.handleKeyDown(event);
    }
    onKeyDown && onKeyDown(event);
  };

  const handleOnKeyUp = (event) => {
    const { onKeyUp } = otherProps;
    !disabled && setIsPressed(false);
    onKeyUp && onKeyUp(event);
  };

  const handleClick = (event) => {
    const { onClick } = otherProps;
    event.preventDefault();
    if (!disabled) {
      setPressedColor(getPressedColor(resolvedTheme, variant, true));
    }
    onClick && onClick(event);
  };

  const handleMouseDown = (event) => {
    const { onMouseDown } = otherProps;
    !disabled && setIsPressed(true);
    onMouseDown && onMouseDown(event);
  };
  const handleMouseUp = (event) => {
    const { onMouseUp } = otherProps;
    !disabled && setIsPressed(false);
    onMouseUp && onMouseUp(event);
  };
  const handleMouseLeave = (event) => {
    const { onMouseLeave } = otherProps;
    !disabled && setIsPressed(false);
    onMouseLeave && onMouseLeave(event);
  };
  const handleBlur = (event) => {
    const { onBlur } = otherProps;
    !disabled && setIsPressed(false);
    onBlur && onBlur(event);
  };

  const classes = classnames({
    [`${LIST_ITEM_TABABLE_ITEM}-${listContext.uniqueListId}`]: !disabled,
    [LIST_ITEM_SELECTED]: !disabled && selected,
  });
  const isCompact = stepFormContext.variant === 'compact';
  const isParent = children !== undefined;

  const animatedChildrenDiv = (
    <Styled.AnimatedSubStep ref={heightRef}>
      {children}
    </Styled.AnimatedSubStep>
  );

  const renderExpandedContent = () =>
    expandedTransition.map(({ item, key, props: openedStyles }) =>
      item && (
        <Styled.SubStepArea
          as={animated.ul}
          key={key}
          style={{
            margin: 0,
            padding: 0,
            ...openedStyles,
            visibility:
              openedStyles.height &&
              openedStyles.height.interpolate(o => (o === 0 ? 'hidden' : 'visible'))
          }}
          $isOpen={isOpen}
          $rtl={RTL}
        >
          {animatedChildrenDiv}
        </Styled.SubStepArea>
      ));

  const content = (
    <>
      <Styled.StepWrapper
        as="li"
        ref={ref}
        rtl={RTL}
        disabled={disabled}
        $theme={resolvedTheme}
        $variant={variant}
        $hasDetails={details !== undefined && !isCompact}
        $numberOfLines={numberOfLines}
        $isCompact={isCompact}
        $isParent={isParent}
        $hoverEffect={stepFormContext.hoverEffect}
        $isOpen={isOpen}
        data-step={lastStep && LAST_STEP_SELECTOR}
        {...otherProps}
        onKeyDown={handleOnKeyDown}
        onKeyUp={handleOnKeyUp}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onBlur={handleBlur}
      >
        <DeprecatedRippleEffect
          as="div"
          ref={rippleRef}
          disabled={disabledRipple || disabled || resolvedDisableAnimation}
          style={{ height: '100%', width: '100%' }}
          rippleStyle={{ backgroundColor: pressedColor, ...rippleStyle }}
        >
          <Styled.Step
            ref={combinedRefs}
            className={classes}
            tabIndex={-1}
            rtl={RTL}
            $theme={resolvedTheme}
            $hasDetails={details !== undefined && !isCompact}
            $isCompact={isCompact}
            $isParent={isParent}
            $variant={variant}
            $numberOfLines={numberOfLines}
            onClick={(event) => { event.preventDefault(); }}
            {...otherInternalStepProps}
          >
            <StepContext.Provider value={{ disabled, variant, isOpen, isLinear, isPressed }}>
              {isParent && <StepDropDownButton disableAnimation={resolvedDisableAnimation} isOpen={isOpen} />}
              <Styled.StepIndicatorContainer $isParent={isParent}>
                {indicator || getIndicatorForVariant(variant, stepNumber, resolvedTheme, indicatorProps)}
                <div style={{ height: SPACING_12 }} />
                {!isCompact && !isOpen && <StepLine style={{ marginLeft: SPACING_12 }} $numberOfLines={numberOfLines} />} {/* partial step line 12px below indicator */}
              </Styled.StepIndicatorContainer>
              <StepTextContainer>
                <Tooltip {...TOOLTIP_PROPS}>
                  <StepTitle theme={resolvedTheme.TITLE} isPressed={isPressed} variant={variant} disabled={disabled} isLinear={isLinear} isParent={isParent}>{title}</StepTitle>
                  {details && !isCompact && (
                    <StepDetail
                      ref={stepDetailsRef}
                      theme={resolvedTheme.DETAIL}
                      isPressed={isPressed}
                      enableTooltip={handleEnableTooltip}
                    >
                      {details}
                    </StepDetail>
                  )}
                </Tooltip>
              </StepTextContainer>
            </StepContext.Provider>
          </Styled.Step>
        </DeprecatedRippleEffect>
      </Styled.StepWrapper>
      {!isCompact && !isOpen && <StepLine $numberOfLines={0} />}
      <StepContext.Provider value={{ disabled, variant, isOpen, disableAnimation: resolvedDisableAnimation }}>
        {renderExpandedContent()}
      </StepContext.Provider>
    </>
  );

  return content;
}));

Step.defaultProps = {
  indicator: undefined,
  indicatorProps: {},
  stepNumber: undefined,
  details: undefined,
  children: undefined,
  lastStep: false,
  isOpen: false,
  selected: false,
  variant: 'future',
  disabled: false,
  theme: {},
  animationProps: undefined,
  disableAnimation: undefined,
  disabledRipple: undefined,
  rippleStyle: {},
  internalStepProps: {},
  disableTooltip: true,
  tooltipProps: {},
  onClick: undefined,
  onKeyDown: undefined,
  onKeyUp: undefined,
  onMouseDown: undefined,
  onMouseUp: undefined,
  onMouseLeave: undefined,
  onBlur: undefined,
};

Step.displayName = 'Step';

export { Step as default, StepContext };
